import { PropsWithChildren, useState } from 'react';
import {
    Box,
    Button,
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    Icon,
    ResponsiveImage,
    Row,
    SquareImageContainer,
    Typography,
    tokensRaw,
} from '@vp/swan';
import styled from 'styled-components';
import { PreviewModal, PreviewModalVariant, download } from '@99designs/design-services-common';
import { StructuredBriefFile } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { useViewContext } from '../../ViewContext';

const StyledResponsiveImage = styled(ResponsiveImage)`
    object-fit: contain;
    cursor: zoom-in;
    width: 100%;

    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    max-width: 100%;
    max-height: 100%;
`;

export const IconContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${tokensRaw.SwanSemSpace5};
`;

export const FileCTA = styled(Button)`
    top: ${tokensRaw.SwanSemSpace3};
    right: ${tokensRaw.SwanSemSpace3};
    position: absolute;
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: 1;
`;

export const ImageContainer = styled(SquareImageContainer)`
    border-radius: ${tokensRaw.SwanSemSpace3};
    overflow: hidden;

    &:hover ${FileCTA} {
        opacity: 1;
        cursor: pointer;
    }
`;

interface FilesFieldViewProps {
    filesValue: StructuredBriefFile[];
}

export function Review(props: FilesFieldViewProps) {
    const { isCollaborationMode } = useViewContext();
    if (isCollaborationMode) {
        return <View {...props} />;
    }
    return (
        <FlexBox gap={3}>
            {props.filesValue.map((file) =>
                file.previewable && file.previewUrl ? (
                    <FluidImage
                        key={`file-${file.name}`}
                        src={file.previewUrl}
                        style={{
                            height: '64px',
                            width: '64px',
                            borderRadius: tokensRaw.SwanSemBorderRadiusStandard,
                        }}
                        alt={file.name}
                    />
                ) : (
                    <Icon iconType="fileGeneric" size="60p" />
                )
            )}
        </FlexBox>
    );
}

function trimFileName(name: string, len = 17): string {
    return name.length > len ? `${name.slice(0, len - 3)}...${name.slice(-3)}` : name;
}

export function View(props: FilesFieldViewProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [currentSlide, setCurrentSlide] = useState(0);

    const referenceFiles = props.filesValue.map((value) => {
        return {
            url: value.downloadUrl,
            filename: value.name,
            previewable: value.previewable,
        };
    });
    const previewableFiles = referenceFiles.filter((file) => file.previewable);
    const unpreviewableFiles = referenceFiles.filter((file) => !file.previewable);

    if (referenceFiles.length === 0) {
        return null;
    }

    return (
        <>
            <PreviewModal
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                currentSlide={currentSlide}
                setCurrentSlide={setCurrentSlide}
                previewFiles={previewableFiles}
                variant={PreviewModalVariant.REFERENCE_FILE}
                onDownload={(file) => download(file.url, file.filename ?? 'file')}
            />
            <GridContainer>
                <Row>
                    {previewableFiles.map((previewableFile, i) => (
                        <FileDisplayWrapper
                            name={trimFileName(previewableFile.filename)}
                            url={previewableFile.url}
                        >
                            <StyledResponsiveImage
                                src={previewableFile.url}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    setCurrentSlide(i);
                                    setIsOpen(!isOpen);
                                }}
                            />
                        </FileDisplayWrapper>
                    ))}
                    {unpreviewableFiles.map((unpreviewableFile) => (
                        <FileDisplayWrapper
                            name={unpreviewableFile.filename}
                            url={unpreviewableFile.url}
                        >
                            <IconContainer>
                                <Icon iconType="fileGeneric" size="32p" marginBottom={'2'} />

                                <Typography fontSize="xsmall" fontWeight="bold" marginBottom={'2'}>
                                    {__('Preview unavailable')}
                                </Typography>
                            </IconContainer>
                        </FileDisplayWrapper>
                    ))}
                </Row>
            </GridContainer>
        </>
    );
}

interface FileDisplayWrapperProps extends PropsWithChildren {
    name?: string;
    url: string;
}

export function FileDisplayWrapper(props: FileDisplayWrapperProps) {
    return (
        <Column span={4} padding={'4'}>
            <ImageContainer bgc="strong">
                <FileCTA
                    onClick={() => download(props.url, props.name ?? 'file')}
                    buttonShape="round"
                    compactMode
                    skin="secondary"
                    aria-label="download"
                >
                    <Icon iconType="download" size="20p" />
                </FileCTA>

                {props.children}
            </ImageContainer>
            {props.name && (
                <Box paddingY={'3'}>
                    <Typography fontSize="xsmall">{props.name}</Typography>
                </Box>
            )}
        </Column>
    );
}
